import { useState, useEffect, useCallback } from 'react';
import api from '../../api';

/**
 * Custom hook for managing food/recipe suggestions
 * Handles fetching, filtering, and pagination of suggestions
 */
export const useSuggestions = (type = 'food', initialStatus = 'pending') => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // Determine API endpoint and query params based on type
  const getEndpointAndParams = useCallback(() => {
    switch (type) {
      case 'food':
        return {
          endpoint: '/suggested-foods',
          params: { status: statusFilter, isLabelFood: false }
        };
      case 'foodLabel':
        return {
          endpoint: '/suggested-foods',
          params: { status: statusFilter, isLabelFood: true }
        };
      case 'recipe':
        return {
          endpoint: '/suggested-recipes',
          params: { status: statusFilter }
        };
      default:
        return {
          endpoint: '/suggested-foods',
          params: { status: statusFilter, isLabelFood: false }
        };
    }
  }, [type, statusFilter]);

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { endpoint, params } = getEndpointAndParams();

      // Build query string
      const queryParams = new URLSearchParams(params).toString();
      const url = `${endpoint}?${queryParams}`;

      const response = await api.get(url);

      if (response.data && response.data.success) {
        const data = response.data.data || [];
        setSuggestions(data);
        setTotalCount(data.length);
      } else {
        throw new Error('Failed to fetch suggestions');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error fetching suggestions';
      setError(errorMsg);
      console.error('Error fetching suggestions:', err);
    } finally {
      setLoading(false);
    }
  }, [getEndpointAndParams]);

  // Fetch suggestions on mount and when dependencies change
  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  // Paginated suggestions
  const paginatedSuggestions = suggestions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return {
    suggestions: paginatedSuggestions,
    allSuggestions: suggestions,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalCount,
    refetch: fetchSuggestions
  };
};
